import { spawn } from "node:child_process";
import { readFileSync, writeFileSync, mkdirSync } from "node:fs";
import { homedir } from "node:os";
import { join, dirname } from "node:path";

/**
 * Lightweight update notifier (no `update-notifier` dependency tree). On each run
 * it reads a cached "latest version" result and, if that is newer than the running
 * version, prints a one-line nudge to stderr. If the cache is stale or missing it
 * spawns a detached background process to refresh it for the *next* run, so the
 * check never adds latency. Fail-soft and silent in non-interactive / agent / CI
 * contexts: an update check must never block or break the CLI.
 */

const PKG = "aethereum";
const REGISTRY = "https://registry.npmjs.org/aethereum/latest";
const TTL_MS = 24 * 60 * 60 * 1000; // check at most once a day

function cachePath(home: string): string {
  return join(home, ".aethereum", "update-check.json");
}

/** Parse "1.2.3" (ignoring any -prerelease suffix) into [major, minor, patch]. */
function parse(v: string): [number, number, number] | null {
  const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(v.trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** true if `latest` is strictly newer than `current` (semver major.minor.patch). */
export function isNewer(latest: string, current: string): boolean {
  const a = parse(latest);
  const b = parse(current);
  if (!a || !b) return false;
  for (let i = 0; i < 3; i++) {
    if (a[i] > b[i]) return true;
    if (a[i] < b[i]) return false;
  }
  return false;
}

export interface UpdateCheckOptions {
  currentVersion: string;
  env?: NodeJS.ProcessEnv;
  isTTY?: boolean;
  home?: string;
  color?: boolean;
  /** test seam: skip spawning the background refresh */
  spawnRefresh?: boolean;
}

export function checkForUpdates(opts: UpdateCheckOptions): void {
  try {
    const env = opts.env ?? process.env;
    const isTTY = opts.isTTY ?? Boolean(process.stdout.isTTY);
    // Never nag in agent / CI / piped / opt-out contexts.
    if (
      !isTTY ||
      env.NO_UPDATE_NOTIFIER ||
      env.CI ||
      env.AETHEREUM_AGENT ||
      env.AETHEREUM_JSON
    ) {
      return;
    }

    const home = opts.home ?? homedir();
    const file = cachePath(home);

    let cache: { lastCheck?: number; latest?: string } = {};
    try {
      cache = JSON.parse(readFileSync(file, "utf8")) as typeof cache;
    } catch {
      /* no cache yet */
    }

    // Already know a newer version -> show the nudge now.
    if (cache.latest && isNewer(cache.latest, opts.currentVersion)) {
      const color = opts.color ?? supportsAnsi(env);
      const g = (s: string) => (color ? `\x1b[32m${s}\x1b[0m` : s);
      const dim = (s: string) => (color ? `\x1b[2m${s}\x1b[0m` : s);
      process.stderr.write(
        `\n  ${g("⬆ update available")} ${dim(`${opts.currentVersion} → ${cache.latest}`)}\n` +
          `  run  ${g(`npm i -g ${PKG}@latest`)}\n\n`,
      );
    }

    // Refresh the cache in the background when stale/missing (for the next run).
    const fresh = typeof cache.lastCheck === "number" && Date.now() - cache.lastCheck < TTL_MS;
    if (!fresh && opts.spawnRefresh !== false) {
      refreshInBackground(file);
    }
  } catch {
    /* fail-soft: never let an update check break the CLI */
  }
}

function supportsAnsi(env: NodeJS.ProcessEnv): boolean {
  if (env.NO_COLOR) return false;
  if (env.FORCE_COLOR) return true;
  return Boolean(process.stdout.isTTY);
}

function refreshInBackground(file: string): void {
  try {
    mkdirSync(dirname(file), { recursive: true });
    // A tiny detached child fetches the registry and writes the cache, then exits.
    // Unref'd so it never holds the CLI process open.
    const script =
      `const{writeFileSync}=require('fs');` +
      `fetch(${JSON.stringify(REGISTRY)}).then(r=>r.json()).then(j=>{` +
      `if(j&&j.version)writeFileSync(${JSON.stringify(file)},JSON.stringify({lastCheck:Date.now(),latest:j.version}));` +
      `}).catch(()=>{});`;
    const child = spawn(process.execPath, ["-e", script], {
      detached: true,
      stdio: "ignore",
      windowsHide: true,
    });
    child.unref();
  } catch {
    /* ignore */
  }
}
